// Catalog.ID Client — Authenticator Key Sheet
// ============================================================================
// New in round 8 (registration-restructure §15.6): the account's TOTP secret on
// paper. The entropy ceremony mints it alongside the password, the supersecret
// and the XRPL key; finalize stores it on the node; this sheet is its printed
// form, and the loss path is the Account Unlock Sheet, not a strip of one-time
// backup codes.
//
// The booklet's "QR only where the payload is not typeable" rule is bent here,
// deliberately and in the safe direction: BOTH forms print. The 32 base32
// characters in groups of four are what a member types into an app that cannot
// scan, and the `otpauth://` QR is the import path every authenticator app on
// the market expects. Black only, like every other sheet.
//
// Pure and node-importable (the sample renderer and the fixture generator build
// it outside a browser), so no `?raw`/`?url` imports here.
//
// PAGE BUDGET: one A4 page (the assembler's one-page-per-builder contract). The
// first cut overflowed — the QR at 210 pt plus four numbered steps and four
// bullets pushed "Keep This Sheet Safe" onto a second page and silently shifted
// every page number after it. The budget is set by the DELEGATE variant, whose
// account header carries three extra rows (Organization / Domain / Role) — some
// 39 pt more than the personal one — so measure that one, not the personal
// sheet, after any change. Re-run apps/cid/dev/render-booklet.ts --png. TRAP: pdfmake's bundled Roboto has no "→" — an arrow prints as a
// missing-glyph box, so say "in Settings, under Authenticator" in words.

import type { Content } from 'pdfmake/interfaces';
import { qrToSvg } from '../qr-svg';
import { formatAuthenticatorKey, otpauthUri } from '../../crypto/totp';
import { accountHeader, type AccountHeaderData } from './shared';

export interface AuthenticatorKeyPageData {
    header: AccountHeaderData;
    /** The 20-byte TOTP secret the node holds for this account. */
    secret: Uint8Array;
}

/** QR side in points. 150 pt over 69 modules (error correction H, 4-module
 *  quiet zone) is 0.77 mm per module at print size — comfortably scannable, and
 *  it is the number that lets the DELEGATE variant of this sheet, whose account
 *  header carries three extra rows, still finish on one page. */
const QR_SIZE = 150;

/** Space under each of the sheet's blocks. */
const BLOCK_GAP = 8;

export function buildAuthenticatorKeyPage(data: AuthenticatorKeyPageData): Content[] {
    const uri = otpauthUri(data.header.cid, data.secret);

    return [
        { text: 'Authenticator Key', style: 'pageTitle' },
        accountHeader(data.header),

        {
            text: 'This is the shared secret that authenticator apps turn into the six-digit login '
                + 'codes you type after your password. It was generated on your own device during '
                + 'registration, together with the account’s other keys.',
            style: 'value',
            margin: [0, 0, 0, 6],
        },

        {
            svg: qrToSvg(new TextEncoder().encode(uri)),
            fit: [QR_SIZE, QR_SIZE], alignment: 'center', margin: [0, 0, 0, 4],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'Key', style: 'section' },
                { text: formatAuthenticatorKey(data.secret), style: 'credential', margin: [0, 2, 0, 2] },
                {
                    text: 'The same value as the code above. Type these characters into an '
                        + 'authenticator app that cannot scan; the spaces are only for reading.',
                    style: 'note',
                },
            ],
            margin: [0, 0, 0, BLOCK_GAP],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'How to use it', style: 'section' },
                {
                    ol: [
                        'Scan the code above with any authenticator app — Google Authenticator, '
                            + 'Microsoft Authenticator, Authy, 1Password and Bitwarden all read it.',
                        'Turn the login code on: during registration when you are offered it, or '
                            + 'later in Settings, under Authenticator.',
                        'Signing in then asks for your password and for the six digits the app shows.',
                        'A new phone is the same scan again — this sheet is all it needs.',
                    ],
                    style: 'value',
                },
            ],
            margin: [0, 0, 0, BLOCK_GAP],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'What it is not', style: 'section' },
                {
                    text: 'This key opens no keys and no data. It adds a second check to your '
                        + 'password, and nothing else — recovery still needs the supersecret, and '
                        + 'unlocking a login still needs your Account Unlock Sheet. Until you turn '
                        + 'the login code on, this key is dormant: it protects nothing and blocks '
                        + 'nothing.',
                    style: 'value',
                },
            ],
            margin: [0, 0, 0, BLOCK_GAP],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'Keep This Sheet Safe', style: 'section' },
                {
                    ul: [
                        'Anyone holding this key can produce your codes — keep it with the rest of '
                            + 'this kit, and do not photograph or scan it.',
                        'If it was ever copied, have Settings issue a new key: the replacement sheet '
                            + 'makes this one useless.',
                        'If you lose both the phone and this sheet, your Account Unlock Sheet issues '
                            + 'a new key.',
                    ],
                    style: 'note',
                },
            ],
        },
    ];
}
